import memoize from 'memoize-one';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import React, { Fragment, PureComponent, memo } from 'react';
import { FlexView, SectionList, Separator, Text, Col } from '@cuemath/comps';

import Link from 'comps/link';
import { openModal } from 'actions/modal';
import { InfoIcon, BlockIcon, AssessmentIcon, TestIcon } from 'comps/icon';
import { PointerText as ContentText, CenterText as HeaderText } from 'comps/styled-text';
import MODAL from 'constants/modal';
import COLORS from 'constants/colors';
import { HighlightedRow } from 'comps/styled-row';
import { NewWindowLink } from 'comps/cue-link';
import { suffixKeys, keyNames, tableKeys } from '../block-content/worksheet/data-keys';

const ContentCard = connect(
  null,
  dispatch => ({
    actions: bindActionCreators({ openModal }, dispatch),
  }),
)(
  memo(({ actions, content, selected }) => {
    const onContentIconClick = () => {
      const commonKeys = ['id', 'code', 'name', 'description', 'is_active'];

      actions.openModal(
        'table',
        {
          size: 'custom',
          width: MODAL.WIDTH,
          height: MODAL.HEIGHT,
        },
        {
          tableData: content.block ? content.block : content.assessment,
          title: content.block
            ? `Block: ${content.block.code}`
            : `Assessment: ${content.assessment.code}`,
          tableKeys: content.block ? commonKeys : tableKeys,
          suffixKeys,
          keyName: keyNames,
        },
      );
    };

    const getContentName = entity =>
      entity.block ? entity.block.name : entity.assessment.name;

    const getContentCode = entity =>
      entity.block ? entity.block.code : entity.assessment.code;

    return (
      <HighlightedRow
        backgroundColor={selected ? COLORS.HIGHLIGHT : 'clear'}
        alignItems="center"
        gap={4}
        gutter
      >
        <Col>
          {content.block ? <BlockIcon size={14} /> : <AssessmentIcon size={14} />}
        </Col>
        <Col gutter={4}>
          <ContentText>
            {getContentName(content)} ({getContentCode(content)})
          </ContentText>
        </Col>
        {content.assessment ? (
          <Col autoRight>
            <NewWindowLink
              href={`https://leap-teacher.cuemath.com/worksheet/${
                content.assessment.id
              }/?activityReference=${content.assessment.learnosity_reference}`}
              target="_blank"
            >
              <TestIcon size={14} />
            </NewWindowLink>
          </Col>
        ) : null}
        <Col autoRight>
          <InfoIcon size={14} onClick={onContentIconClick} />
        </Col>
      </HighlightedRow>
    );
  }),
);

export default class ChapterContent extends PureComponent {
  static propTypes = {
    chapterContent: PropTypes.array.isRequired, // eslint-disable-line react/forbid-prop-types
    match: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  };

  getChapterContent = memoize(contents =>
    contents.map(content => ({
      ...content,
      versions: content.block ? content.block.versions : [],
    })),
  );

  sectionKeyExtractor = chapterContent => chapterContent.id;

  itemKeyExtractor = chapterContent => chapterContent.id;

  isCurrentContentSelected = (blockVersionId, versions) => {
    if (!blockVersionId) return false;

    for (const index in versions) {
      if (blockVersionId === versions[index].id) {
        return true;
      }
    }
    return false;
  };

  renderSectionHeader = section => {
    const { versions } = section;
    const { match } = this.props;
    return (
      <ContentCard
        content={section}
        selected={this.isCurrentContentSelected(match.params.blockVersionId, versions)}
      />
    );
  };

  renderSectionFooter = () => <Separator />;

  renderItem = ({ item: version, section }) => {
    const { match } = this.props;
    const selectedVersionId = match.params.blockVersionId;
    const {
      params: { programCode, versionId },
    } = match;

    return (
      <Fragment>
        <HighlightedRow
          backgroundColor={
            selectedVersionId && selectedVersionId === version.id
              ? COLORS.HIGHLIGHT
              : 'clear'
          }
          gutter={64}
        >
          <Col>
            <Link
              to={`/curriculum/${programCode}/chapter-contents/${versionId}/block-contents/${
                version.id
              }`}
            >
              <Text type={version.is_live ? 'light' : 'normal'}>
                {' '}
                Version: {version.version}{' '}
              </Text>
            </Link>
          </Col>
        </HighlightedRow>
      </Fragment>
    );
  };

  render() {
    const { chapterContent, match } = this.props;
    const memoizedChapterContent = this.getChapterContent(chapterContent);
    return (
      <FlexView>
        <HeaderText> Blocks and Assessments </HeaderText>
        {memoizedChapterContent.length ? (
          <SectionList
            match={match}
            data={memoizedChapterContent}
            dataKey="versions"
            sectionKeyExtractor={this.sectionKeyExtractor}
            itemKeyExtractor={this.itemKeyExtractor}
            renderSectionHeader={this.renderSectionHeader}
            renderSectionFooter={this.renderSectionFooter}
            renderItem={this.renderItem}
          />
        ) : (
          <Text align="center"> No Blocks or Assessment Present</Text>
        )}
      </FlexView>
    );
  }
}
